import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FileText, BookOpen, BarChart2, Users, Plus,
  Edit2, Trash2, Eye, Save, X, Search, Filter,
  TrendingUp, TrendingDown, Minus, Image, Upload,
  ChevronLeft, ChevronRight, ChevronDown, Settings, Rss, RefreshCw,
  ExternalLink, Calendar, Twitter, FileClock, PenTool, Loader2, Film, Instagram, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';
import XMonitorManager from '../components/admin/XMonitorManager';
import DraftsManager from '../components/admin/DraftsManager';
import PublishedManager from '../components/admin/PublishedManager';
import EditionsManager from '../components/admin/EditionsManager';
import StaffManagementPage from './StaffManagementPage';

const SERIF_FONT = "'Playfair Display', Georgia, serif";

// ============================================
// ADMIN PAGE - Panel Completo de Gestión
// Optimizado para móvil (Fitts, Hick, Miller)
// ============================================
const AdminPage = () => {
  const { isAdmin, user, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('xmonitor');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Redirect if not admin (but wait for auth to finish loading)
  useEffect(() => {
    // Only redirect after loading is complete AND user is not admin
    if (!loading && !isAdmin) {
      navigate('/');
    }
  }, [isAdmin, loading, navigate]);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando permisos...</p>
        </div>
      </div>
    );
  }

  // Show access denied if not admin (after loading)
  if (!isAdmin) {
    return null; // Will redirect in useEffect
  }

  // Show content immediately - no global loading screen
  // Individual components handle their own loading states

  // Tabs agrupados por función (Ley de Hick)
  const tabs = [
    { id: 'xmonitor', label: 'Monitor X', icon: Twitter, group: 'contenido' },
    { id: 'create', label: 'Crear Artículo', icon: PenTool, group: 'contenido' },
    { id: 'drafts', label: 'Por Revisar', icon: FileClock, group: 'contenido' },
    { id: 'dossiers', label: 'Publicados', icon: FileText, group: 'contenido' },
    { id: 'editions', label: 'Ediciones', icon: BookOpen, group: 'contenido' },
    { id: 'reels', label: 'Reels', icon: Film, group: 'contenido' },
    { id: 'staff', label: 'Equipo', icon: Users, group: 'sistema' },
    { id: 'scraping', label: 'Generar', icon: Rss, group: 'sistema' },
  ];

  const currentTab = tabs.find(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-page">
      {/* Header - Light theme */}
      <div className="py-4 md:py-6 sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg md:text-2xl font-bold tracking-wide text-gray-900" style={{ fontFamily: SERIF_FONT }}>
                PANEL EDITORIAL
              </h1>
              <p className="text-gray-500 text-xs md:text-sm mt-0.5">Gestión de contenido</p>
            </div>
            {/* Mobile tab selector */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex items-center gap-2 px-4 py-2 rounded-lg min-h-[48px] text-gray-700 bg-gray-100 border border-gray-200"
            >
              {currentTab && <currentTab.icon className="w-5 h-5" />}
              <span className="text-sm font-medium">{currentTab?.label}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute w-full z-30 bg-white border-b border-gray-200 shadow-lg">
          <div className="p-4 grid grid-cols-3 gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`flex flex-col items-center gap-1 p-3 rounded-lg min-h-[72px] transition-colors ${
                    activeTab === tab.id
                      ? 'bg-red-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100 bg-gray-50'
                  }`}
                >
                  <Icon className="w-6 h-6" />
                  <span className="text-xs font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Desktop Tabs */}
      <div className="hidden md:block bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1 overflow-x-auto py-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'bg-red-600 text-white'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 md:px-4 py-4 md:py-6 pb-24 md:pb-6">
        {activeTab === 'xmonitor' && <XMonitorManager />}
        {activeTab === 'create' && <CreateArticleManager />}
        {activeTab === 'drafts' && <DraftsManager />}
        {activeTab === 'dossiers' && <PublishedManager />}
        {activeTab === 'editions' && <EditionsManager />}
        {activeTab === 'reels' && <ReelsManager />}
        {activeTab === 'staff' && <StaffManagementPage embedded />}
        {activeTab === 'scraping' && <ScrapingManager />}
      </div>
    </div>
  );
};

// ============================================
// DOSSIERS MANAGER - CRUD Completo (Mobile Optimized)
// ============================================
const DossiersManager = () => {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDossier, setEditingDossier] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSector, setFilterSector] = useState('all');

  useEffect(() => {
    fetchDossiers();
  }, []);

  const fetchDossiers = async () => {
    try {
      const data = await api.getDossiers();
      setDossiers(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (dossierId) => {
    if (!window.confirm('¿Eliminar esta noticia?')) return;
    try {
      await api.deleteDossier(dossierId);
      fetchDossiers();
    } catch (error) {
      alert('Error al eliminar');
    }
  };

  const handleSave = async (data) => {
    try {
      if (data.dossier_id) {
        await api.updateDossier(data.dossier_id, data);
      } else {
        await api.createDossier(data);
      }
      setEditingDossier(null);
      setShowCreateModal(false);
      fetchDossiers();
    } catch (error) {
      alert('Error al guardar');
    }
  };

  const filteredDossiers = dossiers.filter(d => {
    const matchesSearch = d.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSector = filterSector === 'all' || d.sector === filterSector;
    return matchesSearch && matchesSector;
  });

  // Limitar a 7 items en móvil (Ley de Miller)
  const displayedDossiers = filteredDossiers.slice(0, window.innerWidth < 768 ? 7 : 50);

  return (
    <div>
      {/* Toolbar - Optimizado para móvil */}
      <div className="space-y-3 mb-4">
        {/* Search - Full width */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Buscar noticias..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-11 h-12 text-base"
          />
        </div>
        
        {/* Filter + Create button - Side by side */}
        <div className="flex gap-2">
          <Select value={filterSector} onValueChange={setFilterSector}>
            <SelectTrigger className="flex-1 h-12">
              <SelectValue placeholder="Filtrar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="política">Política</SelectItem>
              <SelectItem value="economía">Economía</SelectItem>
              <SelectItem value="negocios">Negocios</SelectItem>
              <SelectItem value="actualidad">Actualidad</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={() => setShowCreateModal(true)} 
            className="bg-black hover:bg-gray-800 h-12 px-4 min-w-[120px]"
          >
            <Plus className="w-5 h-5 mr-1" /> Nueva
          </Button>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {displayedDossiers.map((dossier) => (
          <div 
            key={dossier.dossier_id} 
            className="bg-white rounded-xl border border-gray-200 p-4"
          >
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm line-clamp-2 mb-2">{dossier.title}</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-xs">{dossier.sector || 'general'}</Badge>
                  <Badge className={`text-xs ${dossier.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {dossier.status === 'published' ? 'Activo' : 'Borrador'}
                  </Badge>
                </div>
              </div>
            </div>
            {/* Action buttons - Large touch targets (Ley de Fitts) */}
            <div className="flex gap-2 mt-3 pt-3 border-t">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setEditingDossier(dossier)}
                className="flex-1 h-11"
              >
                <Edit2 className="w-4 h-4 mr-1" /> Editar
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleDelete(dossier.dossier_id)}
                className="h-11 px-4 text-[#1e3a5f] border-red-200 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
        {filteredDossiers.length > 7 && (
          <p className="text-center text-sm text-gray-500 py-2">
            Mostrando 7 de {filteredDossiers.length} • Usa el buscador para filtrar
          </p>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Título</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Sector</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Estado</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Fecha</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8 text-gray-400">Cargando...</td></tr>
            ) : filteredDossiers.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-gray-400">No hay noticias</td></tr>
            ) : (
              filteredDossiers.slice(0, 50).map((d) => (
                <tr key={d.dossier_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 line-clamp-1">{d.title}</p>
                    <p className="text-xs text-gray-500">{d.subject}</p>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline">{d.sector || '-'}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={d.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                      {d.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {d.created_at ? new Date(d.created_at).toLocaleDateString('es') : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => window.open(`/dossiers/${d.dossier_id}`, '_blank')}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingDossier(d)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-[#1e3a5f]" onClick={() => handleDelete(d.dossier_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      <DossierModal
        open={showCreateModal || !!editingDossier}
        onClose={() => { setShowCreateModal(false); setEditingDossier(null); }}
        dossier={editingDossier}
        onSave={handleSave}
      />
    </div>
  );
};

// ============================================
// DOSSIER MODAL - Crear/Editar
// ============================================
const DossierModal = ({ open, onClose, dossier, onSave }) => {
  const [form, setForm] = useState({
    title: '',
    subject: '',
    sector: 'actualidad',
    country: '',
    status: 'draft',
    is_premium: false,
    executive_summary: [''],
    what_changed_today: '',
    cover_image: '',
    author: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (dossier) {
      setForm({
        ...dossier,
        executive_summary: dossier.executive_summary || ['']
      });
    } else {
      setForm({
        title: '',
        subject: '',
        sector: 'actualidad',
        country: '',
        status: 'draft',
        is_premium: false,
        executive_summary: [''],
        what_changed_today: '',
        cover_image: '',
        author: ''
      });
    }
  }, [dossier]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dossier ? 'Editar Noticia' : 'Nueva Noticia'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Título *</label>
            <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Sujeto/Persona</label>
              <Input value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">País</label>
              <Input value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Sector</label>
              <Select value={form.sector} onValueChange={(v) => setForm({...form, sector: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="política">Política</SelectItem>
                  <SelectItem value="economía">Economía</SelectItem>
                  <SelectItem value="negocios">Negocios</SelectItem>
                  <SelectItem value="actualidad">Actualidad</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado</label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="review">En revisión</SelectItem>
                  <SelectItem value="published">Publicado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Autor</label>
              <Input value={form.author || ''} onChange={(e) => setForm({...form, author: e.target.value})} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Resumen Ejecutivo</label>
            <Textarea 
              value={form.executive_summary?.join('\n') || ''} 
              onChange={(e) => setForm({...form, executive_summary: e.target.value.split('\n')})} 
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">¿Qué cambió hoy?</label>
            <Textarea value={form.what_changed_today || ''} onChange={(e) => setForm({...form, what_changed_today: e.target.value})} rows={2} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">URL de Imagen de Portada</label>
            <Input value={form.cover_image || ''} onChange={(e) => setForm({...form, cover_image: e.target.value})} placeholder="https://..." />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="is_premium" checked={form.is_premium} onChange={(e) => setForm({...form, is_premium: e.target.checked})} />
            <label htmlFor="is_premium" className="text-sm">Contenido Premium</label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" className="bg-gray-900" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};


// ============================================
// REELS MANAGER
// ============================================
const ReelsManager = () => {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genTaskId, setGenTaskId] = useState(null);
  const [genStatus, setGenStatus] = useState(null);
  const [topNews, setTopNews] = useState([]);
  const [loadingNews, setLoadingNews] = useState(true);
  const [selectedDossiers, setSelectedDossiers] = useState([]);
  const [newsPage, setNewsPage] = useState(0);
  const [publishingId, setPublishingId] = useState(null);
  const [previewReel, setPreviewReel] = useState(null);
  const [autopost, setAutopost] = useState(null);
  const [savingAutopost, setSavingAutopost] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const videoSrc = (url) => (url && url.startsWith('http') ? url : `${API_BASE}${url || ''}`);

  const fetchReels = async () => {
    try {
      const res = await api.listReels();
      setReels(res.reels || []);
    } catch {} finally { setLoading(false); }
  };

  const fetchTopNews = async () => {
    setLoadingNews(true);
    try {
      const res = await api.getProductionTopNews();
      const news = res.news || [];
      setTopNews(news);
      setNewsPage(0);
      return news;
    } catch { return []; } finally { setLoadingNews(false); }
  };

  const fetchAutopost = async () => {
    try {
      const cfg = await api.getReelsAutopostConfig();
      setAutopost(cfg);
    } catch {}
  };

  useEffect(() => { fetchReels(); fetchTopNews(); fetchAutopost(); }, []);

  const handleSaveAutopost = async (next) => {
    setSavingAutopost(true);
    try {
      const cfg = { ...autopost, ...next, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
      const res = await api.saveReelsAutopostConfig(cfg);
      setAutopost(res);
    } catch (error) {
      alert('Error guardando la configuración: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingAutopost(false);
    }
  };

  // Poll generation task
  useEffect(() => {
    if (!genTaskId) return;
    const interval = setInterval(async () => {
      try {
        const s = await api.getAITaskStatus(genTaskId);
        setGenStatus(s);
        if (s.status === 'completed' || s.status === 'failed') {
          clearInterval(interval);
          setGenerating(false);
          fetchReels();
          fetchTopNews();
        }
      } catch { clearInterval(interval); setGenerating(false); }
    }, 5000);
    return () => clearInterval(interval);
  }, [genTaskId]);

  const toggleDossier = (id) => {
    setSelectedDossiers(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);
  };

  const handleGenerate = async () => {
    if (selectedDossiers.length === 0) return;
    setGenerating(true); setGenStatus(null);
    try {
      const res = await api.generateReel({ dossier_ids: selectedDossiers });
      if (res.success) {
        setGenTaskId(res.task_id);
        setGenStatus({ status: 'processing', progress: 0 });
        setSelectedDossiers([]);
        fetchReels();
        fetchTopNews();
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
      setGenerating(false);
    }
  };

  const handleDelete = async (reelId) => {
    if (!window.confirm('¿Eliminar este reel? Sus noticias quedarán cargadas y seleccionadas para que puedas reutilizarlas.')) return;
    const res = await api.deleteReel(reelId);
    fetchReels();
    const fresh = await fetchTopNews();
    // Reutilizar las noticias del reel borrado: las ponemos al frente (página 1)
    // y pre-seleccionamos las que quedaron libres.
    const reused = (res && res.reused_news) || [];
    if (reused.length) {
      const reusedIds = new Set(reused.map(n => n.dossier_id));
      const rest = fresh.filter(n => !reusedIds.has(n.dossier_id));
      setTopNews([...reused, ...rest]);
      setSelectedDossiers(reused.filter(n => !n.used_in_reel).map(n => n.dossier_id));
      setNewsPage(0);
    }
  };

  const handlePublishIG = async (reelId) => {
    if (!window.confirm('¿Publicar este reel en Instagram ahora?')) return;
    setPublishingId(reelId);
    try {
      const res = await api.publishReelInstagram(reelId);
      if (res.success) {
        alert('Reel publicado en Instagram ✓');
        fetchReels();
      } else {
        alert('Error al publicar: ' + (res.error || 'desconocido'));
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPublishingId(null);
    }
  };

  // Paginación del selector de noticias ("ver más" con botones de siguiente)
  const NEWS_PER_PAGE = 6;
  const totalNewsPages = Math.max(1, Math.ceil(topNews.length / NEWS_PER_PAGE));
  const safeNewsPage = Math.min(newsPage, totalNewsPages - 1);
  const pagedNews = topNews.slice(safeNewsPage * NEWS_PER_PAGE, safeNewsPage * NEWS_PER_PAGE + NEWS_PER_PAGE);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Film className="w-5 h-5" />
          Reels
        </h2>
        <p className="text-sm text-gray-500 mt-1">Genera Reels con la marca de La Tech y publícalos en Instagram</p>
      </div>

      {/* Publicación automática cada N horas (cron configurable) */}
      <div className="bg-white rounded-xl border p-6 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Calendar className="w-4 h-4 text-purple-600" />
              Publicación automática
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Cada X horas se genera un reel con la mejor noticia de X (sin repetir usadas) y se publica solo en Instagram.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={autopost?.interval_hours || 4}
              onChange={(e) => setAutopost(prev => ({ ...prev, interval_hours: Number(e.target.value) }))}
              disabled={savingAutopost}
              className="border rounded-lg px-2 py-1.5 text-sm"
              title="Cada cuántas horas se publica un reel"
            >
              {[2, 3, 4, 6, 8, 12, 24].map(n => <option key={n} value={n}>cada {n}h</option>)}
            </select>
            <Button
              onClick={() => handleSaveAutopost({})}
              disabled={savingAutopost || !autopost}
              variant="outline"
              className="text-xs"
            >
              {savingAutopost ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Guardar'}
            </Button>
            <button
              onClick={() => handleSaveAutopost({ enabled: !autopost?.enabled })}
              disabled={savingAutopost || !autopost}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${autopost?.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
              title={autopost?.enabled ? 'Desactivar' : 'Activar'}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${autopost?.enabled ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
          </div>
        </div>
        <p className="text-[11px] text-gray-400">
          {autopost?.enabled
            ? `Activo: cada ${autopost.interval_hours || 4}h (${autopost.timezone}).`
            : 'Desactivado.'}
          {autopost?.last_run && ` Última corrida: ${new Date(autopost.last_run).toLocaleString('es')} — ${autopost.last_result || ''}`}
        </p>
      </div>

      {/* Selector de noticias por interacciones en X */}
      <div className="bg-white rounded-xl border p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold flex items-center gap-2">
            <Twitter className="w-4 h-4 text-sky-500" />
            Noticias con más interacciones en X
          </h3>
          <button onClick={fetchTopNews} className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Actualizar
          </button>
        </div>

        {loadingNews ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : topNews.length === 0 ? (
          <p className="text-sm text-gray-400 py-4">No hay noticias publicadas todavía.</p>
        ) : (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {pagedNews.map(n => {
              const selected = selectedDossiers.includes(n.dossier_id);
              const used = n.used_in_reel;
              const m = n.tweet_metrics || {};
              return (
                <button
                  key={n.dossier_id}
                  onClick={() => !used && toggleDossier(n.dossier_id)}
                  disabled={generating || used}
                  title={used ? 'Esta noticia ya se usó en un reel' : undefined}
                  className={`text-left flex gap-3 p-3 rounded-lg border transition-colors ${used ? 'border-gray-100 opacity-50 cursor-not-allowed' : selected ? 'border-black bg-gray-50 ring-1 ring-black' : 'border-gray-200 hover:border-gray-400'}`}
                >
                  {n.cover_image && (
                    <img src={videoSrc(n.cover_image)} alt="" className="w-16 h-16 object-cover rounded-md flex-shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold line-clamp-2">
                      {used && <span className="inline-block text-[10px] font-bold uppercase bg-gray-200 text-gray-500 rounded px-1.5 py-0.5 mr-1.5 align-middle">Usada</span>}
                      {!used && n.is_recent === false && <span className="inline-block text-[10px] font-bold uppercase bg-amber-100 text-amber-700 rounded px-1.5 py-0.5 mr-1.5 align-middle" title="Publicada hace más de 24 horas">+24h</span>}
                      {n.title}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500">
                      <span className="uppercase text-gray-400">{n.category}</span>
                      {n.tweet_published && (
                        <>
                          <span title="Impresiones">👁 {m.impressions || 0}</span>
                          <span title="Likes" className="flex items-center gap-0.5"><Heart className="w-3 h-3" /> {m.likes || 0}</span>
                          <span title="Retweets">🔁 {m.retweets || 0}</span>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {totalNewsPages > 1 && (
            <div className="flex items-center justify-center gap-4 pt-3">
              <button
                onClick={() => setNewsPage(Math.max(0, safeNewsPage - 1))}
                disabled={safeNewsPage === 0}
                className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" /> Anteriores
              </button>
              <span className="text-xs text-gray-500 tabular-nums">
                {safeNewsPage * NEWS_PER_PAGE + 1}–{Math.min((safeNewsPage + 1) * NEWS_PER_PAGE, topNews.length)} de {topNews.length}
              </span>
              <button
                onClick={() => setNewsPage(Math.min(totalNewsPages - 1, safeNewsPage + 1))}
                disabled={safeNewsPage >= totalNewsPages - 1}
                className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Siguientes <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          </>
        )}

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handleGenerate} disabled={generating || selectedDossiers.length === 0} className="bg-black text-white hover:bg-gray-800">
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Film className="w-4 h-4 mr-2" />}
            {generating ? 'Generando...' : selectedDossiers.length > 1 ? `Generar ${selectedDossiers.length} reels` : 'Generar Reel'}
          </Button>
          <p className="text-xs text-gray-400">
            {selectedDossiers.length > 1
              ? `Un reel por noticia (${selectedDossiers.length} videos) · ~15-18 min c/u · el render va de a uno en cola`
              : 'Seleccioná una o más noticias · un reel por cada una · ~15-18 min · el render va de a uno en cola'}
          </p>
        </div>

        {genStatus && (
          <div className={`p-4 rounded-lg border ${genStatus.status === 'completed' ? 'bg-green-50 border-green-200' : genStatus.status === 'failed' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
            {genStatus.status === 'processing' && (
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-blue-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">Renderizando el reel…</p>
                  <p className="text-xs text-blue-600 mt-1">Esto tarda varios minutos. Podés salir de esta pestaña; el reel aparecerá abajo al terminar.</p>
                  <div className="w-full bg-blue-200 rounded-full h-1.5 mt-2">
                    <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${genStatus.progress || 5}%` }} />
                  </div>
                </div>
              </div>
            )}
            {genStatus.status === 'completed' && (
              <div>
                <p className="text-sm font-medium text-green-800">Reel generado exitosamente</p>
                <p className="text-xs text-green-600 mt-1">{genStatus.result?.title}</p>
              </div>
            )}
            {genStatus.status === 'failed' && (
              <div>
                <p className="text-sm font-medium text-red-800">Error al generar el Reel</p>
                <p className="text-xs text-red-600 mt-1">{genStatus.error}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reels Grid */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : reels.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <Film className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay reels generados</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {reels.map(reel => {
            const isReady = reel.status === 'ready';
            return (
              <div key={reel.reel_id} className="bg-white rounded-xl border overflow-hidden">
                <div className="relative mx-auto" style={{ width: '220px', padding: '12px 0' }}>
                  <div className="bg-black rounded-[24px] overflow-hidden border-4 border-gray-800 shadow-xl" style={{ aspectRatio: '9/16' }}>
                    {isReady ? (
                      <button onClick={() => setPreviewReel(reel)} className="relative w-full h-full group" title="Ver preview">
                        <video src={videoSrc(reel.video_url)} className="w-full h-full object-cover" preload="metadata" playsInline muted />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                          <span className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                            <svg viewBox="0 0 24 24" className="w-5 h-5 ml-0.5 fill-gray-900"><path d="M8 5v14l11-7z" /></svg>
                          </span>
                        </span>
                      </button>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-900">
                        <div className="text-center">
                          {reel.status === 'generating' ? <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto" /> : <FileText className="w-8 h-8 text-red-400 mx-auto" />}
                          <p className="text-xs text-gray-400 mt-2">{reel.status === 'generating' ? 'Generando...' : 'Fallido'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Status badge */}
                  <div className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold ${isReady ? 'bg-green-500 text-white' : reel.status === 'generating' ? 'bg-blue-500 text-white' : 'bg-red-500 text-white'}`}>
                    {isReady ? 'LISTO' : reel.status === 'generating' ? 'GENERANDO' : 'ERROR'}
                  </div>
                </div>
                <div className="p-4 border-t">
                  <h4 className="font-bold text-sm line-clamp-2 mb-1">{reel.title}</h4>
                  {reel.status === 'failed' && reel.error && (
                    <p className="text-xs text-red-500 line-clamp-2">{reel.error}</p>
                  )}
                  {isReady && (
                    <button
                      onClick={() => handlePublishIG(reel.reel_id)}
                      disabled={publishingId === reel.reel_id || reel.instagram_published}
                      className={`w-full mt-3 flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg transition-colors ${reel.instagram_published ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-pink-600 text-white hover:bg-pink-700'}`}
                    >
                      {publishingId === reel.reel_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Instagram className="w-3.5 h-3.5" />}
                      {reel.instagram_published ? 'Publicado en IG ✓' : 'Publicar en Instagram'}
                    </button>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    {isReady && (
                      <a href={videoSrc(reel.video_url)} target="_blank" rel="noreferrer" download className="flex-1 text-center text-xs font-medium py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors">
                        Descargar
                      </a>
                    )}
                    <button onClick={() => handleDelete(reel.reel_id)} className="text-xs text-red-500 hover:text-red-700 py-2 px-3">
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview estilo Instagram: video 9:16 a la izquierda, info a la derecha */}
      {previewReel && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setPreviewReel(null)}>
          <button onClick={() => setPreviewReel(null)} className="absolute top-4 right-4 text-white/80 hover:text-white" title="Cerrar">
            <X className="w-7 h-7" />
          </button>
          <div className="flex flex-col md:flex-row bg-black rounded-lg overflow-hidden shadow-2xl max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
            {/* Video: contenido, nunca rellena la pantalla */}
            <div className="bg-black flex items-center justify-center" style={{ height: 'min(85vh, 800px)', aspectRatio: '9/16' }}>
              <video
                src={videoSrc(previewReel.video_url)}
                className="w-full h-full object-contain"
                controls
                autoPlay
                playsInline
              />
            </div>
            {/* Panel de info estilo IG */}
            <div className="bg-white w-full md:w-96 flex flex-col">
              <div className="flex items-center gap-3 px-4 py-3 border-b">
                <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-gray-900 to-gray-600 flex items-center justify-center text-white text-xs font-bold">LT</div>
                <span className="text-sm font-semibold">latechnologia</span>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                <div className="flex gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-gray-900 to-gray-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">LT</div>
                  <div className="text-sm">
                    <span className="font-semibold mr-2">latechnologia</span>
                    <span className="text-gray-800 whitespace-pre-line">{previewReel.description || previewReel.title}</span>
                    <p className="text-xs text-gray-400 mt-2">
                      {previewReel.duration ? `${previewReel.duration}s · ` : ''}
                      {previewReel.dossier_ids?.length > 1 ? `${previewReel.dossier_ids.length} noticias · ` : ''}
                      {previewReel.created_at ? new Date(previewReel.created_at).toLocaleDateString('es', { day: 'numeric', month: 'long' }) : ''}
                    </p>
                  </div>
                </div>
                {previewReel.instagram_published && previewReel.instagram_url && (
                  <a href={previewReel.instagram_url} target="_blank" rel="noreferrer" className="block text-xs text-blue-600 hover:underline">
                    Ver publicación en Instagram →
                  </a>
                )}
              </div>
              <div className="border-t px-4 py-3 space-y-2">
                {!previewReel.instagram_published && (
                  <button
                    onClick={() => { setPreviewReel(null); handlePublishIG(previewReel.reel_id); }}
                    className="w-full flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg bg-pink-600 text-white hover:bg-pink-700"
                  >
                    <Instagram className="w-3.5 h-3.5" /> Publicar en Instagram
                  </button>
                )}
                <a href={videoSrc(previewReel.video_url)} target="_blank" rel="noreferrer" download className="block w-full text-center text-xs font-medium py-2 bg-black text-white rounded-lg hover:bg-gray-800">
                  Descargar
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================
// ============================================
// CREATE ARTICLE MANAGER - AI + Firecrawl
// ============================================
const CreateArticleManager = () => {
  const [topic, setTopic] = useState('');
  const [instructions, setInstructions] = useState('');
  const [creating, setCreating] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const [recentArticles, setRecentArticles] = useState([]);

  useEffect(() => {
    api.getDraftDossiers().then(res => setRecentArticles((res.drafts || []).slice(0, 5))).catch(() => {});
  }, []);

  // Poll task status
  useEffect(() => {
    if (!taskId) return;
    const interval = setInterval(async () => {
      try {
        const status = await api.getAITaskStatus(taskId);
        setTaskStatus(status);
        if (status.status === 'completed' || status.status === 'failed') {
          setCreating(false);
          clearInterval(interval);
          if (status.status === 'completed') {
            api.getDraftDossiers().then(res => setRecentArticles((res.drafts || []).slice(0, 5))).catch(() => {});
          }
        }
      } catch {
        clearInterval(interval);
        setCreating(false);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [taskId]);

  const handleCreate = async () => {
    if (!topic.trim()) return;
    setCreating(true);
    setTaskStatus(null);
    try {
      const res = await api.createArticleFromTopic(topic.trim(), instructions.trim());
      if (res.success) {
        setTaskId(res.task_id);
        setTaskStatus({ status: 'processing', progress: 0 });
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2">
          <PenTool className="w-5 h-5" />
          Crear Artículo con IA
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Escribe un tema y la IA investigará con Firecrawl y generará el artículo completo
        </p>
      </div>

      <div className="bg-white rounded-xl border p-6 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Tema o titular de la noticia</label>
          <Input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Ej: Apple lanza su nuevo chip M5 con rendimiento revolucionario"
            className="text-base"
            disabled={creating}
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Instrucciones editoriales (opcional)</label>
          <Textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Ej: Enfócate en el impacto para consumidores, incluye comparación con M4..."
            rows={3}
            disabled={creating}
          />
        </div>

        <Button onClick={handleCreate} disabled={creating || !topic.trim()} className="bg-black text-white hover:bg-gray-800 w-full sm:w-auto">
          {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PenTool className="w-4 h-4 mr-2" />}
          {creating ? 'Generando...' : 'Investigar y Crear Artículo'}
        </Button>

        {/* Progress */}
        {taskStatus && (
          <div className={`p-4 rounded-lg border ${taskStatus.status === 'completed' ? 'bg-green-50 border-green-200' : taskStatus.status === 'failed' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
            {taskStatus.status === 'processing' && (
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">Investigando y generando artículo...</p>
                  <p className="text-xs text-blue-600 mt-1">La IA está buscando fuentes con Firecrawl y redactando el artículo</p>
                  <div className="w-full bg-blue-200 rounded-full h-1.5 mt-2">
                    <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${taskStatus.progress || 10}%` }} />
                  </div>
                </div>
              </div>
            )}
            {taskStatus.status === 'completed' && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <FileText className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-green-800">Artículo creado como borrador</p>
                  <p className="text-xs text-green-600">{taskStatus.result?.title}</p>
                </div>
              </div>
            )}
            {taskStatus.status === 'failed' && (
              <p className="text-sm text-red-700">Error: {taskStatus.error || 'No se pudo generar el artículo'}</p>
            )}
          </div>
        )}
      </div>

      {/* Link to full editor with manual mode */}
      <div className="bg-white rounded-xl border p-6 text-center">
        <p className="text-sm text-gray-500 mb-3">¿Prefieres escribir manualmente?</p>
        <Link to="/admin/write">
          <Button variant="outline">
            <PenTool className="w-4 h-4 mr-2" />
            Abrir editor completo (Manual + IA)
          </Button>
        </Link>
      </div>

      {/* Recent drafts */}
      {recentArticles.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-700 mb-3">Borradores recientes</h3>
          <div className="space-y-2">
            {recentArticles.map(d => (
              <div key={d.dossier_id} className="bg-white rounded-lg border p-3 flex items-center gap-3">
                <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{d.title}</p>
                  <p className="text-xs text-gray-500">{d.sector || 'tecnología'}</p>
                </div>
                <Badge variant="outline" className="text-xs">Borrador</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// INFLUENCE INDEX MANAGER - CRUD Completo
// ============================================
const InfluenceManager = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const data = await api.getInfluenceIndex();
      setEntries(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('¿Eliminar esta entrada?')) return;
    try {
      await api.deleteInfluenceEntry(entryId);
      fetchEntries();
    } catch (error) {
      alert('Error al eliminar');
    }
  };

  const handleSave = async (data) => {
    try {
      if (data.entry_id) {
        await api.updateInfluenceEntry(data.entry_id, data);
      } else {
        await api.createInfluenceEntry(data);
      }
      setEditingEntry(null);
      setShowCreateModal(false);
      fetchEntries();
    } catch (error) {
      alert('Error al guardar');
    }
  };

  return (
    <div>
      <div className="flex justify-between mb-6">
        <h2 className="text-xl font-bold">Índice de Influencia</h2>
        <Button onClick={() => setShowCreateModal(true)} className="bg-gray-900">
          <Plus className="w-4 h-4 mr-2" /> Nueva Entrada
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">#</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Nombre</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Tipo</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Score</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Movimiento</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-400">Cargando...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-400">No hay entradas</td></tr>
            ) : (
              entries.map((entry, i) => (
                <tr key={entry.entry_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-bold text-gray-400">{i + 1}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{entry.entity_name}</p>
                    <p className="text-xs text-gray-500">{entry.sector}</p>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline">{entry.entity_type}</Badge>
                  </td>
                  <td className="px-4 py-3 font-bold">{entry.score}</td>
                  <td className="px-4 py-3">
                    <span className={`flex items-center gap-1 ${entry.movement > 0 ? 'text-green-600' : entry.movement < 0 ? 'text-[#1e3a5f]' : 'text-gray-400'}`}>
                      {entry.movement > 0 ? <TrendingUp className="w-4 h-4" /> : entry.movement < 0 ? <TrendingDown className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                      {entry.movement > 0 ? '+' : ''}{entry.movement}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditingEntry(entry)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-[#1e3a5f]" onClick={() => handleDelete(entry.entry_id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <InfluenceModal
        open={showCreateModal || !!editingEntry}
        onClose={() => { setShowCreateModal(false); setEditingEntry(null); }}
        entry={editingEntry}
        onSave={handleSave}
      />
    </div>
  );
};

// ============================================
// INFLUENCE MODAL
// ============================================
const InfluenceModal = ({ open, onClose, entry, onSave }) => {
  const [form, setForm] = useState({
    entity_name: '',
    entity_type: 'persona',
    sector: '',
    country: '',
    score: 50,
    previous_score: 50,
    key_facts: ['']
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entry) {
      setForm({ ...entry, key_facts: entry.key_facts || [''] });
    } else {
      setForm({ entity_name: '', entity_type: 'persona', sector: '', country: '', score: 50, previous_score: 50, key_facts: [''] });
    }
  }, [entry]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{entry ? 'Editar Entrada' : 'Nueva Entrada'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nombre *</label>
            <Input value={form.entity_name} onChange={(e) => setForm({...form, entity_name: e.target.value})} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Tipo</label>
              <Select value={form.entity_type} onValueChange={(v) => setForm({...form, entity_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="persona">Persona</SelectItem>
                  <SelectItem value="empresa">Empresa</SelectItem>
                  <SelectItem value="organización">Organización</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sector</label>
              <Input value={form.sector || ''} onChange={(e) => setForm({...form, sector: e.target.value})} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">País</label>
              <Input value={form.country || ''} onChange={(e) => setForm({...form, country: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Score</label>
              <Input type="number" min={0} max={100} value={form.score} onChange={(e) => setForm({...form, score: parseInt(e.target.value)})} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Score Anterior</label>
              <Input type="number" min={0} max={100} value={form.previous_score} onChange={(e) => setForm({...form, previous_score: parseInt(e.target.value)})} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Datos Clave (uno por línea)</label>
            <Textarea 
              value={form.key_facts?.join('\n') || ''} 
              onChange={(e) => setForm({...form, key_facts: e.target.value.split('\n')})} 
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" className="bg-gray-900" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// SCRAPING MANAGER - Flujo Manual Completo
// ============================================
const ScrapingManager = () => {
  // Estados del flujo
  const [step, setStep] = useState(1); // 1: Config, 2: Resultados, 3: Adaptación, 4: Publicación
  const [section, setSection] = useState('política');
  const [keywords, setKeywords] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [location, setLocation] = useState('global');
  
  // Datos
  const [searchResults, setSearchResults] = useState([]);
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [adaptedArticles, setAdaptedArticles] = useState([]);
  
  // Loading states
  const [searching, setSearching] = useState(false);
  const [adapting, setAdapting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  
  // Paso 1: Buscar noticias
  const handleSearch = async () => {
    setSearching(true);
    try {
      const response = await api.manualScrapeSearch({ 
        section, 
        keywords: keywords || '',  // Puede estar vacío
        location,
        date_from: dateFrom || null,
        date_to: dateTo || null
      });
      if (response.success) {
        setSearchResults(response.articles || []);
        setStep(2);
      } else {
        alert('Error al buscar noticias');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al buscar noticias: ' + (error.message || 'Error desconocido'));
    } finally {
      setSearching(false);
    }
  };
  
  // Toggle selección de artículo
  const toggleArticleSelection = (articleId) => {
    const article = searchResults.find(a => a.id === articleId);
    if (!article) return;
    
    if (selectedArticles.find(a => a.id === articleId)) {
      setSelectedArticles(selectedArticles.filter(a => a.id !== articleId));
    } else {
      setSelectedArticles([...selectedArticles, article]);
    }
  };
  
  // Paso 2: Adaptar contenido seleccionado
  const handleAdapt = async () => {
    if (selectedArticles.length === 0) {
      alert('Selecciona al menos un artículo para adaptar');
      return;
    }
    
    setAdapting(true);
    try {
      const response = await api.manualAdaptContent({ 
        articles: selectedArticles, 
        section 
      });
      if (response.success) {
        setAdaptedArticles(response.adapted_articles || []);
        setStep(3);
      } else {
        alert('Error al adaptar contenido');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al adaptar: ' + (error.message || 'Error desconocido'));
    } finally {
      setAdapting(false);
    }
  };
  
  // Editar artículo adaptado
  const updateAdaptedArticle = (id, field, value) => {
    setAdaptedArticles(adaptedArticles.map(a => 
      a.id === id ? { ...a, [field]: value } : a
    ));
  };
  
  // Remover artículo de la lista
  const removeAdaptedArticle = (id) => {
    setAdaptedArticles(adaptedArticles.filter(a => a.id !== id));
  };
  
  // Paso 3: Publicar
  const handlePublish = async () => {
    if (adaptedArticles.length === 0) {
      alert('No hay artículos para publicar');
      return;
    }
    
    if (!window.confirm(`¿Publicar ${adaptedArticles.length} artículo(s)?`)) return;
    
    setPublishing(true);
    try {
      const response = await api.manualPublishArticles({ 
        articles: adaptedArticles,
        generate_images: true
      });
      if (response.success) {
        alert(`Se publicaron ${response.total_created} artículo(s) exitosamente`);
        // Reset todo
        setStep(1);
        setKeywords('');
        setSearchResults([]);
        setSelectedArticles([]);
        setAdaptedArticles([]);
      } else {
        alert('Error al publicar');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al publicar: ' + (error.message || 'Error desconocido'));
    } finally {
      setPublishing(false);
    }
  };
  
  // Reset al paso 1
  const resetFlow = () => {
    setStep(1);
    setSearchResults([]);
    setSelectedArticles([]);
    setAdaptedArticles([]);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <h2 className="text-lg md:text-xl font-bold">Generador de Contenido</h2>
        <p className="text-gray-500 text-xs md:text-sm mt-1">Busca noticias, adáptalas al tono del medio y publícalas</p>
      </div>
      
      {/* Progress Steps - Mobile optimized */}
      <div className="flex items-center justify-between gap-1 mb-6 bg-gray-100 rounded-xl p-2">
        {[
          { num: 1, label: 'Buscar' },
          { num: 2, label: 'Elegir' },
          { num: 3, label: 'Publicar' },
        ].map((s, i) => (
          <div 
            key={s.num}
            className={`flex-1 flex flex-col items-center gap-1 py-2 px-2 rounded-lg text-center transition-colors ${
              step >= s.num 
                ? 'bg-black text-white' 
                : 'bg-transparent text-gray-400'
            }`}
          >
            <span className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= s.num ? 'bg-white/20' : 'bg-gray-200'
            }`}>
              {step > s.num ? '✓' : s.num}
            </span>
            <span className="text-xs font-medium">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Step 1: Configuration */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2 text-sm md:text-base">
            <Settings className="w-5 h-5" />
            Configurar Búsqueda
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Sección destino</label>
              <Select value={section} onValueChange={setSection}>
                <SelectTrigger className="w-full h-12 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="política">Política</SelectItem>
                  <SelectItem value="economía">Economía</SelectItem>
                  <SelectItem value="negocios">Negocios</SelectItem>
                  <SelectItem value="actualidad">Actualidad</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Ubicación</label>
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger className="w-full h-12 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">🌍 Todo el mundo</SelectItem>
                  <SelectItem value="latam">🌎 Latinoamérica</SelectItem>
                  <SelectItem value="usa">🇺🇸 Estados Unidos</SelectItem>
                  <SelectItem value="mexico">🇲🇽 México</SelectItem>
                  <SelectItem value="colombia">🇨🇴 Colombia</SelectItem>
                  <SelectItem value="argentina">🇦🇷 Argentina</SelectItem>
                  <SelectItem value="chile">🇨🇱 Chile</SelectItem>
                  <SelectItem value="peru">🇵🇪 Perú</SelectItem>
                  <SelectItem value="brasil">🇧🇷 Brasil</SelectItem>
                  <SelectItem value="venezuela">🇻🇪 Venezuela</SelectItem>
                  <SelectItem value="ecuador">🇪🇨 Ecuador</SelectItem>
                  <SelectItem value="espana">🇪🇸 España</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Palabras clave (opcional)</label>
              <Textarea 
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="Ej: Trump, elecciones, inflación... o déjalo vacío para últimas noticias"
                rows={2}
                className="text-base"
              />
            </div>
            
            {/* Rango de fechas */}
            <div>
              <label className="block text-sm font-medium mb-2">Rango de fechas (opcional)</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Desde</label>
                  <Input
                    type="date"
                    value={dateFrom ? dateFrom.split('/').reverse().join('-') : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      if (date) {
                        const [year, month, day] = date.split('-');
                        setDateFrom(`${day}/${month}/${year}`);
                      } else {
                        setDateFrom('');
                      }
                    }}
                    className="h-11"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Hasta</label>
                  <Input
                    type="date"
                    value={dateTo ? dateTo.split('/').reverse().join('-') : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      if (date) {
                        const [year, month, day] = date.split('-');
                        setDateTo(`${day}/${month}/${year}`);
                      } else {
                        setDateTo('');
                      }
                    }}
                    className="h-11"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Deja vacío para buscar en todas las fechas
              </p>
            </div>
            
            <Button 
              onClick={handleSearch} 
              disabled={searching}
              className="w-full bg-black hover:bg-gray-800 h-12 text-base"
            >
              {searching ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Buscando...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5 mr-2" />
                  Buscar Noticias
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Search Results */}
      {step === 2 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold flex items-center gap-2 text-sm md:text-base">
              <Rss className="w-5 h-5" />
              {searchResults.length} encontrados
            </h3>
            <Button variant="ghost" size="sm" onClick={resetFlow} className="h-10 px-3">
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <p className="text-xs md:text-sm text-gray-500 mb-4">
            Toca para seleccionar artículos
          </p>
          
          {searchResults.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No se encontraron noticias
            </div>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto mb-4 -mx-1 px-1">
              {searchResults.slice(0, 7).map((article) => (
                <div 
                  key={article.id}
                  className={`p-3 md:p-4 rounded-xl border-2 transition-all min-h-[60px] ${
                    selectedArticles.find(a => a.id === article.id)
                      ? 'border-black bg-gray-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <div 
                      onClick={() => toggleArticleSelection(article.id)}
                      className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors cursor-pointer ${
                        selectedArticles.find(a => a.id === article.id)
                          ? 'border-black bg-black text-white'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {selectedArticles.find(a => a.id === article.id) && '✓'}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p 
                        onClick={() => toggleArticleSelection(article.id)}
                        className="font-medium text-sm line-clamp-2 cursor-pointer"
                      >
                        {article.title}
                      </p>
                      
                      {/* Summary preview */}
                      {article.summary && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{article.summary}</p>
                      )}
                      
                      {/* Meta info: Source, Date, Link */}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge variant="outline" className="text-xs">{article.source}</Badge>
                        
                        {article.published_date ? (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {article.published_date}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Sin fecha
                          </span>
                        )}
                        
                        {/* Link to read article */}
                        <a 
                          href={article.source_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 ml-auto"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Leer
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {searchResults.length > 7 && (
                <p className="text-center text-xs text-gray-400 py-2">
                  Mostrando 7 de {searchResults.length} resultados
                </p>
              )}
            </div>
          )}
          
          {/* Fixed bottom action bar */}
          <div className="flex flex-col gap-2 pt-4 border-t">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">
                {selectedArticles.length} seleccionado(s)
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={resetFlow} className="flex-1 h-12">
                Volver
              </Button>
              <Button 
                onClick={handleAdapt} 
                disabled={adapting || selectedArticles.length === 0}
                className="flex-1 bg-black hover:bg-gray-800 h-12"
              >
                {adapting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Adaptando...
                  </>
                ) : (
                  <>
                    <Edit2 className="w-4 h-4 mr-2" />
                    Adaptar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Adapted Content Review */}
      {step === 3 && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold flex items-center gap-2 text-sm">
                <FileText className="w-5 h-5" />
                {adaptedArticles.length} artículo(s) adaptado(s)
              </h3>
              <Button variant="ghost" size="sm" onClick={resetFlow} className="h-10">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {adaptedArticles.map((article) => (
            <div key={article.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex justify-between items-start mb-3">
                <Badge className="bg-black text-white">{section}</Badge>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-[#1e3a5f] h-10 w-10 p-0"
                  onClick={() => removeAdaptedArticle(article.id)}
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
              </div>
              
              {article.error ? (
                <div className="text-[#1e3a5f] text-sm p-4 bg-red-50 rounded-lg">
                  Error: {article.error}
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">TÍTULO</label>
                    <Input 
                      value={article.title || ''} 
                      onChange={(e) => updateAdaptedArticle(article.id, 'title', e.target.value)}
                      className="font-semibold h-11 text-base"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">RESUMEN</label>
                    <Textarea 
                      value={article.summary || ''} 
                      onChange={(e) => updateAdaptedArticle(article.id, 'summary', e.target.value)}
                      rows={3}
                      className="text-base"
                    />
                  </div>
                  
                  <details className="group">
                    <summary className="cursor-pointer text-xs text-gray-500 py-2">
                      Ver contenido completo ▼
                    </summary>
                    <Textarea 
                      value={article.content || ''} 
                      onChange={(e) => updateAdaptedArticle(article.id, 'content', e.target.value)}
                      rows={5}
                      className="mt-2 text-sm"
                    />
                  </details>
                  
                  <div className="text-xs text-gray-400 pt-2 border-t">
                    Fuente: {article.source}
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {/* Fixed publish bar */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sticky bottom-20 md:bottom-4">
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-center">
                {adaptedArticles.filter(a => !a.error).length} listo(s) para publicar
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1 h-12">
                  Volver
                </Button>
                <Button 
                  onClick={handlePublish} 
                  disabled={publishing || adaptedArticles.filter(a => !a.error).length === 0}
                  className="flex-1 bg-black hover:bg-gray-800 h-12"
                >
                  {publishing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Publicando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Publicar
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
